import { useEffect } from 'react';
import { activeSite } from './sites';

/**
 * Setzt Favicon, Touch-Icon und Manifest auf die des geltenden Auftritts.
 *
 * `public/index.html` ist die gemeinsame Hülle beider Auftritte; die
 * Icon-Adressen stehen dort fest und zeigen auf die Kundenwebsite. Diese Stelle
 * biegt sie um, wenn ein anderer Auftritt gilt — sonst trüge die Kampagnendomain
 * das Symbol der Kundenwebsite im Tab, und genau daran erkennt jemand eine
 * Seite wieder, die er aus dem Radio kennt.
 *
 * **Warum hier und nicht in nginx:** Eine Weiche im Webserver griffe nur in
 * Produktion. Der Entwicklungsserver liefert `public/` unverändert aus, das
 * lokale Bild wäre also dauerhaft ein anderes als das echte — eine Abweichung,
 * die man erst nach dem Live-Gang bemerkt. Im HTML gilt für beide dasselbe.
 *
 * Das Prerendering hält den Zustand fest: Es speichert das DOM, **nachdem**
 * React gelaufen ist. Die statischen Seiten tragen die richtigen Adressen also
 * schon, bevor im Browser irgendetwas ausgeführt wird.
 */
export function useSiteIcons(): void {
  const { icons } = activeSite();

  useEffect(() => {
    // Ein Icon je Auswahl; fehlt eines der Tags in index.html, wird es
    // übersprungen statt nachgebaut — die Hülle bleibt die eine Wahrheit
    // darüber, welche Formate die Seite überhaupt anbietet.
    const zuordnung: [string, string][] = [
      ['link[rel="icon"][sizes="any"]', icons.ico],
      ['link[rel="icon"][sizes="32x32"]', `${icons.dir}/favicon-32.png`],
      ['link[rel="icon"][sizes="64x64"]', `${icons.dir}/favicon-64.png`],
      ['link[rel="apple-touch-icon"]', `${icons.dir}/apple-touch-icon.png`],
      ['link[rel="manifest"]', icons.manifest],
    ];

    const zurueck: Array<() => void> = [];
    for (const [selektor, adresse] of zuordnung) {
      const tag = document.querySelector<HTMLLinkElement>(selektor);
      if (!tag) continue;
      const vorher = tag.getAttribute('href');
      if (vorher === adresse) continue;
      tag.setAttribute('href', adresse);
      zurueck.push(() => {
        if (vorher === null) tag.removeAttribute('href');
        else tag.setAttribute('href', vorher);
      });
    }

    return () => zurueck.forEach((f) => f());
  }, [icons]);
}

export default useSiteIcons;
